import React, { useEffect, useState, useCallback } from 'react';
import { Card, Skeleton, message, Button, Pagination } from 'antd';
import { useNavigate } from 'react-router-dom';
import { getInspectionTemplates } from '../utils/apiService';
import InspectionList from '../components/inspections/InspectionList';
import { ReloadOutlined } from '@ant-design/icons';



const NewInspection = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [searchLoading, setSearchLoading] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [pagination, setPagination] = useState({
    current: 0,
    pageSize: 10,
    total: 0,
    hasNextPage: false
  });
  const [searchTerm, setSearchTerm] = useState('');

  // Load templates with optional search parameters
  const loadTemplates = async (pageIndex = 0, pageSize = 10, search = '', isSearchQuery = false) => {
    try {
      // Only show full page loading on initial load, not during searches
      if (!isSearchQuery) {
        setLoading(true);
      } else {
        setSearchLoading(true);
      }
      
      const response = await getInspectionTemplates({
        pageIndex,
        pageSize,
        searchTerm: search
      });
      
      // Handle the paginated response
      setTemplates(response?.items || []);
      setPagination({
        current: response?.currentPage || 0,
        pageSize: response?.pageSize || 10,
        total: response?.totalCount || 0,
        hasNextPage: response?.hasNextPage || false
      });
    } catch (err) {
      console.error(err);
      message.error('Failed to load templates');
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  // Load templates on initial render
  useEffect(() => {
    loadTemplates(0, pagination.pageSize, '');
  }, []);
  
  
  const handlePageChange = (page, pageSize) => {
    // Convert from 1-based to 0-based index
    const pageIndex = page - 1;
    // Mark as search query to avoid full page loading state
    loadTemplates(pageIndex, pageSize, searchTerm, true);
  };
  
  // Handle search - this is called after debounce in SearchInput
  const handleSearchChange = useCallback((value) => {
    setSearchTerm(value);
    // Trigger search immediately since value is already debounced in SearchInput
    loadTemplates(0, pagination.pageSize, value, true);
  }, [pagination.pageSize]);

  const handleSelectTemplate = (tpl) => {
    const id = tpl?.id || tpl?.templateId || tpl?.guid;
    if (!id) return message.warning('Template ID not found');
    navigate(`/inspections/templates/${id}`);
  };

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <Button 
          onClick={() => loadTemplates(pagination.current, pagination.pageSize, searchTerm)}
          icon={<ReloadOutlined />}
          loading={loading}
        >
          Refresh
        </Button>
      </div>
      {loading ? (
        <Skeleton active />
      ) : (
        <Card>
          <InspectionList 
            templates={templates} 
            onSelect={handleSelectTemplate} 
            onSearch={handleSearchChange}
            searchValue={searchTerm}
            loading={searchLoading} // Only pass search loading state to prevent input focus loss
          />
          {pagination.total > 0 && (
            <div style={{ marginTop: 16, textAlign: 'right' }}>
              <Pagination
                current={pagination.current + 1} // Convert 0-based to 1-based for display
                pageSize={pagination.pageSize}
                total={pagination.total}
                onChange={handlePageChange}
                showSizeChanger
                showTotal={(total, range) => `${range[0]}-${range[1]} of ${total} templates`}
              />
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

export default NewInspection;
